import { 
  useParticipants,
  RoomAudioRenderer,
  ParticipantTile,
  GridLayout,
  useTracks,
  useLocalParticipant,
  useRoomContext
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useEffect, useState, useRef } from 'react';
import CustomControlBar from './CustomControlBar';
import ChatPanel from './ChatPanel';
import SharedBackground from './SharedBackground';
import ParticipantTileWithStats from './ParticipantTileWithStats';

export default function EnhancedVideoConference({ roomName = 'video-room', userIdentity = 'guest' }) {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isBackgroundSelectorOpen, setIsBackgroundSelectorOpen] = useState(false);
  const [isBackgroundEnabled, setIsBackgroundEnabled] = useState(true);
  const [isAudioControlsVisible, setIsAudioControlsVisible] = useState(false); // Track audio controls visibility
  const [audioState, setAudioState] = useState({ hasAudio: false, isMuted: true }); // Default to background off
  // Stats are hidden by default - change this to true to enable stats display
  const [showStats] = useState(false);
  const participants = useParticipants();
  
  // Get tracks using the correct format as shown in the example
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );
  
  // Apply custom styling to the video tiles to make them more transparent
  // and positioned better over the background
  useEffect(() => {
    // Add custom CSS to adjust the video conference layout
    const style = document.createElement('style');
    style.textContent = `
      /* Make participant names white and more visible */
      .lk-participant-name {
        display: none !important;
        color: white !important;
        background-color: rgba(0, 0, 0, 0.6) !important;
        /* padding: 4px 8px !important;
        border-radius: 4px !important; */
        margin: 0 0 8px 8px !important;
      }
      
      /* Custom styles for video conference */
      
      .custom-video-conference {
        background: linear-gradient(135deg, #121212 0%, #1a1a2e 100%) !important;
        position: relative;
        width: 100%;
        height: 100vh;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
        width: 100%;
        position: relative;
      }
      
      .lk-participant-tile {
        background-color: rgba(0, 0, 0, 0.3) !important;
        backdrop-filter: blur(4px);
        border-radius: 12px;
        overflow: hidden; 
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        margin: 8px;
      }
      
      .custom-grid-layout {
        width: 100%;
        height: calc(100vh - var(--control-bar-height));
        transition: width 0.3s ease;
        display: flex;
        justify-content: center;
      }
      
      .custom-grid-layout.chat-open {
        width: 70%;
      }
      
      /* Improve chat panel positioning */
      .chat-panel {
        position: absolute;
        right: 0;
        width: 30%;
        height: calc(100vh - 80px);
        background-color: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(10px);
        border-left: 1px solid rgba(255, 255, 255, 0.1);
        z-index: 10;
      }
      
      .custom-grid-layout .participants-grid {
        width: 100%;
        max-width: 1200px;
      }
      
      .participants-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
        grid-auto-rows: 1fr;
        gap: 16px;
        width: 100%;
        height: 100%;
        padding: 16px;
        max-height: calc(100vh - 100px);
      }
      
      .participant-container {
        position: relative;
        width: 100%;
        height: 0;
        padding-bottom: 56.25%; /* 16:9 aspect ratio */
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 8px;
        overflow: hidden;
      }
      
      .participant-container > div {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
      
      .participant-tile {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      
      .participant-name {
        position: absolute;
        bottom: 10px;
        left: 10px;
        background-color: rgba(0, 0, 0, 0.5);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 14px;
        z-index: 2;
      }
      

      
      .lk-participant-name {
        background-color: rgba(0, 0, 0, 0.5);
        padding: 4px 8px;
        border-radius: 4px;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };
  
  const changeBackground = () => {
    // Toggle the background selector
    setIsBackgroundSelectorOpen(!isBackgroundSelectorOpen);
  };
  
  // Handle background changes from the SharedBackground component
  const handleBackgroundChange = (enabled, backgroundId) => {
    console.log(`Background ${enabled ? 'enabled' : 'disabled'}, ID: ${backgroundId}`);
    setIsBackgroundEnabled(enabled);
  };
  
  // Handle audio state changes from the SharedBackground component
  const handleAudioStateChange = (state) => {
    console.log(`Audio state changed: hasAudio=${state.hasAudio}, isMuted=${state.isMuted}`);
    // Only update state if there's an actual change to prevent infinite loops
    setAudioState(prevState => {
      if (prevState.hasAudio === state.hasAudio && prevState.isMuted === state.isMuted) {
        return prevState; // No change, return previous state
      }
      return state; // Actual change, update the state
    });
  };
  
  // Toggle audio controls visibility
  const toggleAudioControls = () => {
    setIsAudioControlsVisible(prev => !prev);
  };
  
  // Toggle audio mute state
  const toggleAudioMute = () => {
    setAudioState(prevState => ({
      ...prevState,
      isMuted: !prevState.isMuted
    }));
  };
  
  return (
    <div className="custom-video-conference">
      {/* SharedBackground component */}
      <SharedBackground 
        roomName="video-room" 
        isOpen={isBackgroundSelectorOpen} 
        onClose={() => setIsBackgroundSelectorOpen(false)}
        defaultBackgroundId={isBackgroundEnabled ? 'floating-market' : 'off'}
        onBackgroundChange={handleBackgroundChange}
        onAudioStateChange={handleAudioStateChange}
        isAudioControlsVisible={isAudioControlsVisible}
        isMuted={audioState.isMuted}
      />
      {/* Use GridLayout with tracks as shown in the example */}
      <div className={`custom-grid-layout ${isChatOpen ? 'chat-open' : ''}`}>
        <GridLayout 
          tracks={tracks} 
          className="participants-grid"
        >
          <ParticipantTileWithStats showStats={showStats} />
        </GridLayout>
      </div>
      
      {/* Chat panel */}
      <ChatPanel isOpen={isChatOpen} roomName={roomName} userIdentity={userIdentity} />
      
      {/* Custom control bar */}
      <CustomControlBar 
        onToggleChat={toggleChat}
        onChangeBackground={changeBackground}
        isBackgroundEnabled={isBackgroundEnabled}
        audioState={audioState}
        onToggleAudio={toggleAudioControls}
        onToggleMute={toggleAudioMute}
      />
      
      {/* Audio renderer */}
      <RoomAudioRenderer />
    </div>
  );
}
